import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Button,
  Stack,
  Tabs,
  Tab,
  Divider,
  Switch,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { BottomSheet, BottomSheetActions } from './BottomSheet';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import { format, parse, addDays } from 'date-fns';
import { MobileStorePicker, type MobileStore } from './MobileStorePicker';
import { useStoreManagement, Store } from '../../context/StoreManagementContext';
import { useStoresQuery, useInvalidateStores } from '../../hooks/queries';
import { useTimeFormat } from '../../context/TimeFormatContext';
import { updateStoreOccasionHours, addSpecialHours, deleteSpecialHours, type OccasionType } from '../../api';
import {
  DayOfWeek,
  DAYS_ORDER,
  DAY_LABELS,
  DAY_LABELS_SHORT,
  formatOccasionName,
  OCCASION_ORDER,
  OCCASION_PICKER_HELP,
} from '../StoreHours/types';
import { AddSpecialHoursDialog, SpecialHoursEntry } from '../StoreHours/AddSpecialHoursDialog';
import { OccasionIcon } from '../StoreHours/OccasionIcon';
import { HelpTooltip } from '../Onboarding';

interface TimeSlot {
  open: string;
  close: string;
}

interface ParsedDayHours {
  enabled: boolean;
  timeSlots: TimeSlot[];
}

interface ParsedOccasion {
  occasion: string;
  isEnabled: boolean;
  days: Record<DayOfWeek, ParsedDayHours>;
}

export const MobileHours: React.FC = () => {
  const { data: stores = [], isLoading: storesLoading } = useStoresQuery();
  const invalidateStores = useInvalidateStores();
  const { selectedMobileStore, setSelectedMobileStore } = useStoreManagement();
  const { use24HourFormat } = useTimeFormat();
  const selectedStoreNumber = selectedMobileStore;
  const setSelectedStoreNumber = setSelectedMobileStore;
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDay, setEditingDay] = useState<DayOfWeek | null>(null);
  const [editingOccasion, setEditingOccasion] = useState<string | null>(null);
  const [editEnabled, setEditEnabled] = useState(true);
  const [editOpenTime, setEditOpenTime] = useState<Date | null>(null);
  const [editCloseTime, setEditCloseTime] = useState<Date | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Tab state for occasion type
  const [selectedOccasionTab, setSelectedOccasionTab] = useState(0);

  // Special hours state
  const [specialHoursDialogOpen, setSpecialHoursDialogOpen] = useState(false);
  const [editingSpecialHours, setEditingSpecialHours] = useState<SpecialHoursEntry | undefined>();
  const [savingSpecialHours, setSavingSpecialHours] = useState(false);
  const [deletingSpecialHoursId, setDeletingSpecialHoursId] = useState<string | null>(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    entry: SpecialHoursEntry | null;
  }>({ open: false, entry: null });

  // Check if we're on mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Convert stores to mobile format
  const mobileStores: MobileStore[] = stores.map(s => ({
    storeNumber: s.storeNumber,
    name: s.name || s.storeNumber,
    isActive: s.isActive,
  }));

  // Auto-select first store if only one
  useEffect(() => {
    if (mobileStores.length === 1 && !selectedStoreNumber) {
      setSelectedStoreNumber(mobileStores[0].storeNumber);
    }
  }, [mobileStores, selectedStoreNumber]);

  // Update selected store when store number changes
  useEffect(() => {
    if (selectedStoreNumber) {
      const store = stores.find(s => s.storeNumber === selectedStoreNumber);
      setSelectedStore(store || null);
    } else {
      setSelectedStore(null);
    }
  }, [selectedStoreNumber, stores]);

  // Parse hours from store data
  const parsedHours = useMemo((): ParsedOccasion[] => {
    if (!selectedStore?.hours) return [];

    const occasions: ParsedOccasion[] = [];

    // Convert ISO weekday (1-7 where 1=Monday, 7=Sunday) to day name
    const isoDayToName = (isoDay: number): DayOfWeek => {
      const dayMap: Record<number, DayOfWeek> = {
        1: 'MONDAY',
        2: 'TUESDAY',
        3: 'WEDNESDAY',
        4: 'THURSDAY',
        5: 'FRIDAY',
        6: 'SATURDAY',
        7: 'SUNDAY',
      };
      return dayMap[isoDay] || 'MONDAY';
    };

    // Parse duration from HH:MM:SS or HH:MM format to minutes
    const parseDuration = (durationStr: string | number): number => {
      if (typeof durationStr === 'number') return durationStr;
      if (typeof durationStr === 'string' && durationStr.includes(':')) {
        const parts = durationStr.split(':').map(Number);
        if (parts.length >= 2) {
          return parts[0] * 60 + parts[1]; // hours * 60 + minutes
        }
      }
      return Number(durationStr) || 0;
    };

    selectedStore.hours.forEach((occasion: any) => {
      const parsed: ParsedOccasion = {
        occasion: occasion.occasion,
        isEnabled: occasion.isEnabled,
        days: {} as Record<DayOfWeek, ParsedDayHours>,
      };

      // Initialize all days as closed
      DAYS_ORDER.forEach(day => {
        parsed.days[day] = { enabled: false, timeSlots: [] };
      });

      // Parse hours for each day
      if (occasion.hours) {
        occasion.hours.forEach((hour: any) => {
          const timeFrame = hour.timeFrame || hour;

          // Skip special hours (those with date range)
          if (timeFrame.startDate && timeFrame.endDate) {
            return;
          }

          // Get days from timeFrame
          const days = timeFrame.days || [];
          if (!days || days.length === 0) return;

          // Parse intervalStartTime (format: "09:00")
          const intervalStart = timeFrame.intervalStartTime || '00:00';
          const durationMinutes = parseDuration(timeFrame.duration || '0');

          // Calculate start and end times
          const [startHour, startMin] = intervalStart.split(':').map(Number);
          const startMinutes = (startHour || 0) * 60 + (startMin || 0);
          const endMinutes = startMinutes + durationMinutes;

          // Convert to HH:MM strings
          const openStr = `${String(startHour || 0).padStart(2, '0')}:${String(startMin || 0).padStart(2, '0')}`;
          const closeHour = Math.floor(endMinutes / 60) % 24;
          const closeMin = endMinutes % 60;
          const closeStr = `${String(closeHour).padStart(2, '0')}:${String(closeMin).padStart(2, '0')}`;

          days.forEach((dayNum: number) => {
            const dayName = isoDayToName(dayNum);
            // Don't add if this is a blackout
            if (!timeFrame.blackout) {
              parsed.days[dayName].enabled = true;
              parsed.days[dayName].timeSlots.push({
                open: openStr,
                close: closeStr,
              });
            }
          });
        });
      }

      occasions.push(parsed);
    });

    return occasions;
  }, [selectedStore]);

  // Helper functions for parsing special hours
  const parseDurationToMinutes = (durationStr: string | number): number => {
    if (typeof durationStr === 'number') return durationStr;
    if (typeof durationStr === 'string' && durationStr.includes(':')) {
      const parts = durationStr.split(':').map(Number);
      return (parts[0] || 0) * 60 + (parts[1] || 0);
    }
    return Number(durationStr) || 0;
  };

  const parseTimeToMinutes = (time: string): number => {
    const parts = time.split(':').map(Number);
    return (parts[0] || 0) * 60 + (parts[1] || 0);
  };

  const formatMinutesToTime = (minutes: number): string => {
    const h = Math.floor(minutes / 60) % 24;
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  // Calculate open window from blackout entries (for modified hours)
  const calculateOpenWindowFromBlackouts = (
    blackouts: Array<{ intervalStartTime: string; duration: string }>
  ): { openTime: string; closeTime: string } => {
    // Sort blackouts by start time
    const sorted = [...blackouts].sort((a, b) => {
      const aStart = parseTimeToMinutes(a.intervalStartTime);
      const bStart = parseTimeToMinutes(b.intervalStartTime);
      return aStart - bStart;
    });

    // First blackout: 00:00 to openTime → openTime = end of first blackout
    // Second blackout: closeTime to 24:00 → closeTime = start of second blackout
    let openTime = '00:00';
    let closeTime = '24:00';

    if (sorted.length >= 1) {
      const firstEnd = parseTimeToMinutes(sorted[0].intervalStartTime) + parseDurationToMinutes(sorted[0].duration);
      openTime = formatMinutesToTime(firstEnd);
    }

    if (sorted.length >= 2) {
      const secondStart = parseTimeToMinutes(sorted[1].intervalStartTime);
      closeTime = formatMinutesToTime(secondStart);
    }

    return { openTime, closeTime };
  };

  // Parse special hours from store data
  // Handles both full closures (24h blackout) and modified hours (partial blackouts)
  const parsedSpecialHours = useMemo((): SpecialHoursEntry[] => {
    if (!selectedStore?.hours) return [];

    // Group hour entries by occasion + date range to detect modified hours
    const dateRangeMap = new Map<string, {
      occasionType: string;
      startDate: Date;
      endDate: Date;
      hours: Array<{
        intervalStartTime: string;
        duration: string;
        blackout: boolean;
      }>;
    }>();

    selectedStore.hours.forEach((occasion: any) => {
      const occasionType = occasion.occasion;
      if (!occasion.isEnabled || !occasion.hours) return;

      occasion.hours.forEach((hour: any) => {
        const timeFrame = hour.timeFrame || hour;

        // Only process special hours (those with date range)
        if (!timeFrame.startDate || !timeFrame.endDate) return;

        const key = `${occasionType}-${timeFrame.startDate}-${timeFrame.endDate}`;

        if (!dateRangeMap.has(key)) {
          dateRangeMap.set(key, {
            occasionType,
            // Append T00:00:00 to treat as local date, not UTC (prevents off-by-one day bug)
            startDate: new Date(timeFrame.startDate + 'T00:00:00'),
            endDate: new Date(timeFrame.endDate + 'T00:00:00'),
            hours: [],
          });
        }

        dateRangeMap.get(key)!.hours.push({
          intervalStartTime: timeFrame.intervalStartTime || '00:00:00',
          duration: timeFrame.duration || '0',
          blackout: timeFrame.blackout === true,
        });
      });
    });

    // Process each date range to create special hours entries
    const entries: SpecialHoursEntry[] = [];

    dateRangeMap.forEach((data, key) => {
      const blackouts = data.hours.filter(h => h.blackout);

      // Check for full closure: a blackout covering 24 hours (1440 minutes)
      const hasFullDayClosure = blackouts.some(b => {
        const minutes = parseDurationToMinutes(b.duration);
        return minutes >= 1440;
      });

      if (hasFullDayClosure) {
        // Full closure - display as "Closed"
        entries.push({
          id: key,
          name: `Special Hours ${format(data.startDate, 'd MMM')}`,
          startDate: data.startDate,
          endDate: data.endDate,
          isClosed: true,
          openTime: undefined,
          closeTime: undefined,
          occasions: [data.occasionType as OccasionType],
        });
      } else if (blackouts.length > 0) {
        // Modified hours - calculate open window from blackout gaps
        const { openTime, closeTime } = calculateOpenWindowFromBlackouts(blackouts);

        entries.push({
          id: key,
          name: `Special Hours ${format(data.startDate, 'd MMM')}`,
          startDate: data.startDate,
          endDate: data.endDate,
          isClosed: false,
          openTime,
          closeTime,
          occasions: [data.occasionType as OccasionType],
        });
      }
    });

    return entries.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  }, [selectedStore]);

  // Get available occasions - filter by both enabled status and configured OCCASION_ORDER
  const availableOccasions = parsedHours.filter(
    o => o.isEnabled && OCCASION_ORDER.includes(o.occasion as OccasionType)
  );

  // Get today's day
  const today = DAYS_ORDER[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];

  const formatTimeDisplay = (time: string): string => {
    if (!time) return '-';
    try {
      const date = parse(time, 'HH:mm', new Date());
      return use24HourFormat
        ? format(date, 'HH:mm')
        : format(date, 'h:mm a');
    } catch {
      return time;
    }
  };

  const parseTimeToDate = (time: string): Date | null => {
    if (!time) return null;
    try {
      return parse(time, 'HH:mm', new Date());
    } catch {
      return null;
    }
  };

  const handleEditDay = (occasionName: string, day: DayOfWeek) => {
    const occasion = parsedHours.find(o => o.occasion === occasionName);
    if (!occasion) return;

    const dayHours = occasion.days[day];
    const firstSlot = dayHours.timeSlots[0];
    setEditingOccasion(occasionName);
    setEditingDay(day);
    setEditEnabled(dayHours.enabled);
    setEditOpenTime(firstSlot?.open ? parseTimeToDate(firstSlot.open) : parseTimeToDate('09:00'));
    setEditCloseTime(firstSlot?.close ? parseTimeToDate(firstSlot.close) : parseTimeToDate('22:00'));
    setEditDialogOpen(true);
  };

  const handleSaveHours = async () => {
    if (!selectedStoreNumber || !editingOccasion || !editingDay) return;

    setSubmitting(true);
    setError(null);

    try {
      const occasion = parsedHours.find(o => o.occasion === editingOccasion);
      if (!occasion) throw new Error('Occasion not found');

      // Build the hours data for the API
      const hoursData: any = {
        occasion: editingOccasion,
        days: {},
      };

      // Include all days, updating only the edited one
      // Note: This currently only supports editing the first time slot
      DAYS_ORDER.forEach(day => {
        const dayHours = occasion.days[day];
        const firstSlot = dayHours.timeSlots[0];
        if (day === editingDay) {
          hoursData.days[day] = {
            enabled: editEnabled,
            timeSlots: editEnabled && editOpenTime && editCloseTime
              ? [{ open: format(editOpenTime, 'HH:mm'), close: format(editCloseTime, 'HH:mm') }]
              : [],
          };
        } else if (dayHours.enabled && firstSlot) {
          hoursData.days[day] = {
            enabled: true,
            timeSlots: [{ open: firstSlot.open, close: firstSlot.close }],
          };
        } else {
          hoursData.days[day] = {
            enabled: false,
            timeSlots: [],
          };
        }
      });

      await updateStoreOccasionHours(selectedStoreNumber, hoursData);

      setEditDialogOpen(false);
      setSuccess('Hours updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      invalidateStores();
    } catch (err: any) {
      console.error('Failed to update hours:', err);
      setError(err.message || 'Failed to update hours');
    } finally {
      setSubmitting(false);
    }
  };

  // Get available occasion types for special hours dialog
  const availableOccasionTypes = useMemo(() => {
    return availableOccasions.map(o => o.occasion as OccasionType);
  }, [availableOccasions]);

  // Handler for quick "Close Now" action
  const handleCloseNow = () => {
    const today = new Date();
    setEditingSpecialHours({
      name: 'Emergency Closure',
      startDate: today,
      endDate: today,
      isClosed: true,
      occasions: availableOccasionTypes as any,
    });
    setSpecialHoursDialogOpen(true);
  };

  // Handler for quick "Close Today" action
  const handleCloseToday = () => {
    const today = new Date();
    setEditingSpecialHours({
      name: 'Closed Today',
      startDate: today,
      endDate: today,
      isClosed: true,
      occasions: availableOccasionTypes as any,
    });
    setSpecialHoursDialogOpen(true);
  };

  // Handler for quick "Close Tomorrow" action
  const handleCloseTomorrow = () => {
    const tomorrow = addDays(new Date(), 1);
    setEditingSpecialHours({
      name: 'Closed Tomorrow',
      startDate: tomorrow,
      endDate: tomorrow,
      isClosed: true,
      occasions: availableOccasionTypes as any,
    });
    setSpecialHoursDialogOpen(true);
  };

  // Handler for adding/editing special hours
  const handleSaveSpecialHours = async (entry: SpecialHoursEntry) => {
    if (!selectedStoreNumber) return;

    setSavingSpecialHours(true);
    setError(null);

    try {
      // Add special hours for each selected occasion
      for (const occasion of entry.occasions) {
        await addSpecialHours(selectedStoreNumber, {
          occasion,
          startDate: format(entry.startDate, 'yyyy-MM-dd'),
          endDate: format(entry.endDate, 'yyyy-MM-dd'),
          isClosed: entry.isClosed,
          openTime: entry.isClosed ? undefined : entry.openTime,
          closeTime: entry.isClosed ? undefined : entry.closeTime,
        });
      }

      setSpecialHoursDialogOpen(false);
      setEditingSpecialHours(undefined);
      setSuccess('Special hours added successfully');
      setTimeout(() => setSuccess(null), 3000);
      invalidateStores();
    } catch (err: any) {
      console.error('Failed to add special hours:', err);
      setError(err.message || 'Failed to add special hours');
    } finally {
      setSavingSpecialHours(false);
    }
  };

  // Handler for deleting special hours
  const handleDeleteSpecialHours = async (entry: SpecialHoursEntry) => {
    if (!selectedStoreNumber || !entry.occasions) return;

    setDeletingSpecialHoursId(entry.id || null);
    setError(null);

    try {
      // Delete special hours for each occasion
      for (const occasion of entry.occasions) {
        await deleteSpecialHours(selectedStoreNumber, {
          occasion,
          startDate: format(entry.startDate, 'yyyy-MM-dd'),
          endDate: format(entry.endDate, 'yyyy-MM-dd'),
        });
      }

      setDeleteConfirmDialog({ open: false, entry: null });
      setSuccess('Special hours deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      invalidateStores();
    } catch (err: any) {
      console.error('Failed to delete special hours:', err);
      setError(err.message || 'Failed to delete special hours');
    } finally {
      setDeletingSpecialHoursId(null);
    }
  };

  const currentOccasion = availableOccasions[selectedOccasionTab];

  // Show occasion tabs when store is selected and has occasions
  const showOccasionTabs = selectedStoreNumber && !storesLoading && availableOccasions.length > 0;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
      {/* Store Picker */}
      <MobileStorePicker
        stores={mobileStores}
        selectedStore={selectedStoreNumber}
        onStoreChange={setSelectedStoreNumber}
        loading={storesLoading}
      />

      {/* Fixed Occasion Tabs - below store picker */}
      {showOccasionTabs && (
        <>
          <Box
            sx={{
              position: 'fixed',
              top: 'calc(env(safe-area-inset-top, 0px) + 64px)', // Below collapsed store picker
              left: 0,
              right: 0,
              zIndex: 1050,
              bgcolor: 'background.paper',
              boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Tabs
              value={selectedOccasionTab}
              onChange={(_, v) => setSelectedOccasionTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 48,
                flex: 1,
                minWidth: 0,
                '& .MuiTab-root': {
                  minWidth: 'auto',
                  minHeight: 48,
                  px: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#662C8B',
                },
              }}
            >
              {availableOccasions.map((occ) => (
                <Tab
                  key={occ.occasion}
                  label={formatOccasionName(occ.occasion)}
                  icon={<OccasionIcon occasion={occ.occasion} />}
                  iconPosition="start"
                />
              ))}
            </Tabs>
            {/* Info bubble: what Carryout vs Delivery hours control */}
            <Box sx={{ px: 1.5, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <HelpTooltip title={OCCASION_PICKER_HELP} />
            </Box>
          </Box>
          {/* Spacer for fixed tabs */}
          <Box sx={{ height: 48, flexShrink: 0 }} />
        </>
      )}

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 2, mt: 1, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mx: 2, mt: 1, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2, pb: 4 }}>
        {!selectedStoreNumber ? (
          // Empty state - welcome banner in store picker handles this
          null
        ) : storesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : availableOccasions.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No hours configured for this store
            </Typography>
          </Box>
        ) : (
          <>
            {/* Today's Hours Highlight - Compact */}
            {currentOccasion && (
              <Box 
                sx={{ 
                  background: 'linear-gradient(135deg, #662C8B 0%, #8B3FA8 100%)', 
                  color: 'white', 
                  px: 2, 
                  py: 1.5,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.85, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                    Today · {DAY_LABELS_SHORT[today]}
                  </Typography>
                  {currentOccasion.days[today].enabled && currentOccasion.days[today].timeSlots.length > 0 ? (
                    <Stack spacing={0.25}>
                      {currentOccasion.days[today].timeSlots.map((slot, idx) => (
                        <Typography key={idx} variant="h6" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                          {formatTimeDisplay(slot.open)} – {formatTimeDisplay(slot.close)}
                        </Typography>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                      Closed
                    </Typography>
                  )}
                </Box>
                {currentOccasion.days[today].enabled && (
                  <Chip
                    label="Open"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 600,
                      height: 24,
                    }}
                  />
                )}
              </Box>
            )}

            {/* Weekly Schedule - Compact */}
            {currentOccasion && (
              <Box 
                sx={{ 
                  mt: 1.5, 
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    px: 2, 
                    pt: 1.5, 
                    pb: 0.5,
                    display: 'block', 
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                  }}
                >
                  Weekly Schedule
                </Typography>
                <List disablePadding dense>
                  {DAYS_ORDER.map((day, index) => {
                    const dayHours = currentOccasion.days[day];
                    const isToday = day === today;

                    return (
                      <React.Fragment key={day}>
                        <ListItem
                          sx={{
                            py: 0.75,
                            px: 2,
                            bgcolor: isToday ? 'rgba(102, 44, 139, 0.04)' : 'transparent',
                            minHeight: 44,
                          }}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleEditDay(currentOccasion.occasion, day)}
                              sx={{ 
                                color: 'text.disabled',
                                '&:hover': { color: 'primary.main' },
                              }}
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          }
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                            <Typography 
                              variant="body2"
                              fontWeight={isToday ? 600 : 500} 
                              color={isToday ? 'primary.main' : 'text.primary'}
                              sx={{ width: 36, flexShrink: 0 }}
                            >
                              {DAY_LABELS_SHORT[day].slice(0, 3)}
                            </Typography>
                            {isToday && (
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: 'primary.main',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <Box sx={{ flex: 1 }}>
                              {dayHours.enabled && dayHours.timeSlots.length > 0 ? (
                                dayHours.timeSlots.map((slot, idx) => (
                                  <Typography
                                    key={idx}
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={400}
                                  >
                                    {formatTimeDisplay(slot.open)} – {formatTimeDisplay(slot.close)}
                                  </Typography>
                                ))
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="error.main"
                                  fontWeight={500}
                                >
                                  Closed
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </ListItem>
                        {index < DAYS_ORDER.length - 1 && <Divider sx={{ ml: 2, mr: 6 }} />}
                      </React.Fragment>
                    );
                  })}
                </List>
              </Box>
            )}

            {/* Special Hours Section - Compact */}
            <Box 
              sx={{ 
                mt: 1.5,
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
              }}
            >
              {/* Header with Quick Actions */}
              <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <CalendarIcon sx={{ color: '#DE1D3B', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={600}>
                      Special Hours
                    </Typography>
                    <HelpTooltip
                      title="Override regular hours for specific dates. Use for holidays, events, or temporary schedule changes."
                      learnMoreLink="store-hours/special-hours"
                    />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingSpecialHours(undefined);
                      setSpecialHoursDialogOpen(true);
                    }}
                    sx={{ color: 'primary.main' }}
                  >
                    <AddIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>

                {/* Quick Action Buttons - More compact */}
                <Stack direction="row" spacing={0.75}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCloseNow}
                    sx={{
                      bgcolor: '#DE1D3B',
                      '&:hover': { bgcolor: '#b71730' },
                      textTransform: 'none',
                      flex: 1,
                      fontSize: '0.7rem',
                      py: 0.75,
                      px: 1,
                      minWidth: 0,
                    }}
                  >
                    Close Now
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCloseToday}
                    sx={{
                      borderColor: 'grey.300',
                      color: 'text.primary',
                      '&:hover': { borderColor: 'grey.400', bgcolor: 'grey.50' },
                      textTransform: 'none',
                      flex: 1,
                      fontSize: '0.7rem',
                      py: 0.75,
                      px: 1,
                      minWidth: 0,
                    }}
                  >
                    Today
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCloseTomorrow}
                    sx={{
                      borderColor: 'grey.300',
                      color: 'text.primary',
                      '&:hover': { borderColor: 'grey.400', bgcolor: 'grey.50' },
                      textTransform: 'none',
                      flex: 1,
                      fontSize: '0.7rem',
                      py: 0.75,
                      px: 1,
                      minWidth: 0,
                    }}
                  >
                    Tomorrow
                  </Button>
                </Stack>
              </Box>

              <Divider />

              {/* Special Hours List */}
              <Box sx={{ px: 2, py: 1.5 }}>
                {parsedSpecialHours.length === 0 ? (
                  <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No special hours scheduled
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Use quick actions above for emergency closures
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {parsedSpecialHours
                      .filter(entry => entry.occasions?.includes(currentOccasion?.occasion as OccasionType))
                      .map((entry) => {
                      // Compare dates only (not time) - endDate is inclusive (end of day)
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const endDateOnly = new Date(entry.endDate);
                      endDateOnly.setHours(23, 59, 59, 999);
                      const startDateOnly = new Date(entry.startDate);
                      startDateOnly.setHours(0, 0, 0, 0);
                      const isPast = endDateOnly < today;
                      const isActive = startDateOnly <= today && endDateOnly >= today;
                      const isDeleting = deletingSpecialHoursId === entry.id;

                      return (
                        <Paper
                          key={entry.id}
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            border: 1,
                            borderColor: isActive ? '#DE1D3B' : isPast ? 'grey.300' : 'divider',
                            bgcolor: isPast ? 'grey.50' : isActive ? 'rgba(222, 29, 59, 0.04)' : 'background.paper',
                            opacity: isPast ? 0.7 : 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" fontWeight={500} noWrap>
                                  {format(entry.startDate, 'd MMM yyyy')}
                                  {entry.endDate.getTime() !== entry.startDate.getTime() && (
                                    <> - {format(entry.endDate, 'd MMM yyyy')}</>
                                  )}
                                </Typography>
                                {isActive && (
                                  <Chip
                                    label="Active"
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: '0.65rem',
                                      bgcolor: '#DE1D3B',
                                      color: 'white',
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: entry.isClosed ? '#DE1D3B' : 'text.primary',
                                  fontWeight: entry.isClosed ? 500 : 400,
                                }}
                              >
                                {entry.isClosed ? 'Closed' : `${formatTimeDisplay(entry.openTime || '')} - ${formatTimeDisplay(entry.closeTime || '')}`}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => setDeleteConfirmDialog({ open: true, entry })}
                              disabled={isDeleting}
                              sx={{ ml: 1 }}
                            >
                              {isDeleting ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DeleteIcon fontSize="small" color="error" />
                              )}
                            </IconButton>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Box>
            
            {/* Bottom spacer */}
            <Box sx={{ height: 24 }} />
          </>
        )}
      </Box>

      {/* Edit Hours Dialog */}
      <BottomSheet
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title={`Edit ${editingDay ? DAY_LABELS[editingDay] : ''} Hours`}
        subtitle={currentOccasion ? formatOccasionName(currentOccasion.occasion) : undefined}
        actions={
          <BottomSheetActions
            onCancel={() => setEditDialogOpen(false)}
            onConfirm={handleSaveHours}
            confirmText="Save Changes"
            confirmDisabled={submitting}
            confirmLoading={submitting}
            confirmIcon={<CheckIcon />}
          />
        }
      >
        <Stack spacing={2.5}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              bgcolor: editEnabled ? 'success.50' : 'grey.100',
              borderRadius: 2,
              border: 1,
              borderColor: editEnabled ? 'success.200' : 'grey.300',
            }}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                {editEnabled ? 'Store Open' : 'Store Closed'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {editEnabled ? 'Customers can visit' : 'Not accepting customers'}
              </Typography>
            </Box>
            <Switch
              checked={editEnabled}
              onChange={(e) => setEditEnabled(e.target.checked)}
              color="success"
            />
          </Box>

          {editEnabled && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 0.5, display: 'block' }}>
                    OPENING TIME
                  </Typography>
                  <MobileTimePicker
                    value={editOpenTime}
                    onChange={(val) => setEditOpenTime(val)}
                    slotProps={{
                      textField: { 
                        fullWidth: true, 
                        size: 'medium',
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                      },
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 0.5, display: 'block' }}>
                    CLOSING TIME
                  </Typography>
                  <MobileTimePicker
                    value={editCloseTime}
                    onChange={(val) => setEditCloseTime(val)}
                    slotProps={{
                      textField: { 
                        fullWidth: true, 
                        size: 'medium',
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                      },
                    }}
                  />
                </Box>
              </Stack>
            </LocalizationProvider>
          )}
        </Stack>
      </BottomSheet>

      {/* Add Special Hours Dialog */}
      <AddSpecialHoursDialog
        open={specialHoursDialogOpen}
        onClose={() => {
          setSpecialHoursDialogOpen(false);
          setEditingSpecialHours(undefined);
        }}
        onSave={handleSaveSpecialHours}
        existingEntry={editingSpecialHours}
        use24Hour={use24HourFormat}
        saving={savingSpecialHours}
        availableOccasions={availableOccasionTypes as any}
        defaultOccasions={(editingSpecialHours?.occasions || availableOccasionTypes) as any}
        fullScreen={isMobile}
      />

      {/* Delete Confirmation Dialog */}
      <BottomSheet
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, entry: null })}
        title="Delete Special Hours?"
        maxHeight="50vh"
        actions={
          <BottomSheetActions
            onCancel={() => setDeleteConfirmDialog({ open: false, entry: null })}
            onConfirm={() => deleteConfirmDialog.entry && handleDeleteSpecialHours(deleteConfirmDialog.entry)}
            cancelText="Keep"
            confirmText="Delete"
            confirmColor="error"
            confirmDisabled={!!deletingSpecialHoursId}
            confirmLoading={!!deletingSpecialHoursId}
            confirmIcon={<DeleteIcon />}
          />
        }
      >
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'error.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <DeleteIcon sx={{ color: 'error.main', fontSize: 28 }} />
          </Box>
          <Typography variant="body1" sx={{ mb: 1 }}>
            This will permanently remove
          </Typography>
          <Typography variant="subtitle1" fontWeight={600}>
            "{deleteConfirmDialog.entry?.name}"
          </Typography>
          {deleteConfirmDialog.entry && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {format(deleteConfirmDialog.entry.startDate, 'd MMM yyyy')}
              {deleteConfirmDialog.entry.endDate.getTime() !== deleteConfirmDialog.entry.startDate.getTime() && (
                <> – {format(deleteConfirmDialog.entry.endDate, 'd MMM yyyy')}</>
              )}
            </Typography>
          )}
        </Box>
      </BottomSheet>
    </Box>
  );
};
